import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { AuthenticationState } from '../monad/AuthenticationState';

export enum PrivilegeValue {
  RegularUser = 350,
  UserHigh = 400,
  LocalActive = 500,
  LocalStaff = 550,
  LocalAdmin = 600,
  LocalOwner = 650
}

declare module 'oidc-client-ts' {
  interface User {
    isFestivalOfficial(): boolean;
    getGroups(): string[];
    getMembershipNumber(): string | null;
    getPrivilegeValue(): number;
    requireMinimumPrivilege(privilege: PrivilegeValue): boolean;
    isInternalTester(): boolean;
    isDevAdmin(): boolean;
    getUserId(): string;
    getFormattedUserId(): string;
    isMember(): boolean;
  }
}
User.prototype.getGroups = function (): string[] {
  //TODO: need to rename roles to groups in oidc ...
  return this.profile["roles"] as string[];
};

User.prototype.getUserId = function (): string {
  return this.profile.sub;
};

User.prototype.getFormattedUserId = function (): string {
  return "U" + this.profile.sub;
};

User.prototype.isMember = function (): boolean {
  return this.getMembershipNumber() != null;
};

User.prototype.isFestivalOfficial = function (): boolean {
  return this.getGroups().indexOf("group_39984") > -1;
};

User.prototype.getMembershipNumber = function (): string | null {
  return this.profile["membership_number"] as string | null;
};

User.prototype.getPrivilegeValue = function (): number {
  return this.profile["privilege_value"] as number;
};

User.prototype.isDevAdmin = function (): boolean {
  return this.profile.sub === "995594";
};

User.prototype.isInternalTester = function (): boolean {
  const testIds = [
    "995594", //s
    "62504" //th
  ];
  const testGroups = [
    "group_1168" // IT-gruppen
  ];
  
  return testIds.indexOf(this.profile.sub) > -1 ||
         testGroups.filter((grp) => this.getGroups().indexOf(grp) > -1).length > 0
};

User.prototype.requireMinimumPrivilege = function (privilege: PrivilegeValue): boolean {
  const userPrivilege = this.getPrivilegeValue();
  return userPrivilege >= privilege;
};

const oidcSettings: UserManagerSettings = {
  authority: window.location.hostname === 'localhost' ? 'https://porscheklubben-poc.azurewebsites.net' : 'https://login.porsche.nu',
  client_id: window.location.hostname === 'localhost' ? 'localhost' : 'porscheklubben_festival',
  redirect_uri: window.location.protocol + '//' + window.location.host + '/#/callback',
  response_type: 'code',
  scope: 'openid profile offline_access roles',
  post_logout_redirect_uri: window.location.protocol + '//' + window.location.host,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  //loadUserInfo: false
  response_mode: "query",
  includeIdTokenInSilentRenew: true,
  automaticSilentRenew: false
};

export const userManager = new UserManager(oidcSettings);

const login = async () => await userManager.signinRedirect();
const logout = () => userManager.signoutRedirect();
const getUser = () => userManager.getUser();
//const signinCallback = () => userManager.signinRedirectCallback();
const signinCallback = () => userManager.signinCallback(window.location.href.replace(/\/#/, ""));

export interface AuthContextType {
  user: AuthenticationState<User>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  signinCallback: () => Promise<User | undefined>;
  setUser: (user: AuthenticationState<User>) => void;
  refreshToken(): Promise<User | null>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = (): AuthContextType => {
  const authContext = useContext<AuthContextType | undefined>(AuthContext);
  if (!authContext) {
    throw new Error("authContext is undefined, need to be inside AuthProvider.");
  }
  return authContext;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthenticationState<User>>(AuthenticationState.idle());

  const refreshToken = async () => {
    try {
      const user = await userManager.signinSilent();
      if (user) {
        console.log("Silent signin user", user);
        setUser(AuthenticationState.loggedIn(user));
        return user;
      } else {
        console.log("Silent signin no user ..");
        //TODO: set expired or something
        setUser(AuthenticationState.loggedOut());
        return null;
      }
    } catch (error) {
      console.error("Error refreshing token.", error);
      setUser(AuthenticationState.loggedOut());
      userManager.removeUser();
      throw error;
    }
  };

  useEffect(() => {
    getUser()
      .then(user => {
        if (user) {
          const roles = user.profile["roles"] as string[];
          console.log("User expired?", user.expired, roles);

          if (!user.expired) {
            setUser(AuthenticationState.loggedIn(user!));
          } else {
            //setUser(AuthenticationState.loggingIn())
            refreshToken();
          }
        } else {
          setUser(AuthenticationState.loggedOut());
        }
      }
      );
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, signinCallback, setUser, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};